import { ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { Styles, css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

interface ExecFeeDisplayProps {
  originalExecFee?: string;
  aqxpBonusRedemption?: string;
  voucherBonusRedemption?: string;
  suffix?: string;
}

const bonusTextStyles: Styles = {
  textStyle: 'Text/S/Regular',
  color: 'content.default.level-2',
};

export const ExecFeeDisplay = ({
  originalExecFee,
  aqxpBonusRedemption,
  voucherBonusRedemption,
  suffix,
}: ExecFeeDisplayProps) => {
  if (!originalExecFee) return null;

  const hasBonusRedemption = Num(aqxpBonusRedemption ?? '0').gt(0) || Num(voucherBonusRedemption ?? '0').gt(0);
  const bonusRedemptionText = getBonusRedemptionText(aqxpBonusRedemption, voucherBonusRedemption);
  const actualExecFee = hasBonusRedemption
    ? calculateActualExecFee(originalExecFee, aqxpBonusRedemption, voucherBonusRedemption)
    : originalExecFee;

  return (
    <div className={stack({ textAlign: 'right' })}>
      {hasBonusRedemption && (
        <Formatted.Number
          value={originalExecFee}
          zeroString='0.00'
          precision={8}
          toFixed
          css={originalFeeValueStyles}
          suffix={suffix}
        />
      )}
      <Formatted.Number
        value={actualExecFee}
        zeroString='0.00'
        precision={8}
        toFixed
        css={actualExecFeeValueStyles}
        suffix={suffix}
      />
      {bonusRedemptionText && <p className={css(bonusTextStyles)}>{bonusRedemptionText}</p>}
    </div>
  );
};

export const calculateActualExecFee = (
  originalFee: string,
  aqxpRedeemed?: string,
  feeVoucherRedeemed?: string,
): string => {
  return Num(originalFee)
    .minus(aqxpRedeemed ?? '0')
    .minus(feeVoucherRedeemed ?? '0')
    .toString();
};

export const getBonusRedemptionText = (aqxpRedeemed?: string, feeVoucherRedeemed?: string): ReactNode | null => {
  const hasAqxp = Num(aqxpRedeemed ?? '0').gt(0);
  const hasVoucher = Num(feeVoucherRedeemed ?? '0').gt(0);

  if (hasAqxp && hasVoucher) return <FormattedMessage id='Copy.r260128.Feediscount_voucher_bonus' />;
  if (hasAqxp) return <FormattedMessage id='Copy.r260128.Feediscount_bonus' />;
  if (hasVoucher) return <FormattedMessage id='Copy.r260128.Feediscount_voucher' />;
  return null;
};

export const originalFeeValueStyles: Styles = {
  textStyle: 'Text/M/Monospace/Regular',
  color: 'content.default.level-2',
  textDecorationLine: 'line-through',
};

export const actualExecFeeValueStyles: Styles = {
  textStyle: 'Text/M/Monospace/Regular',
  color: 'content.default.level-5',
};
